import { flattenRentalItemRequest, flattenRentalParkingRequest } from '@/api/booking'
import { CALCULATE_PRICE } from '@/api/graphql/new-booking'
import { apolloClient } from '@/plugins/apollo'
import { isoDate } from '@/utils/get-date'
import {
  LIST_NEAR_FUTURE_BOOKINGS,
  LIST_FUTURE_BOOKINGS,
  LIST_PAST_BOOKINGS,
  GET_BOOKING,
  GET_BOOKING_WITH_CODE,
  PRE_CHECK_IN_WITH_CODE,
  PRE_CHECK_IN,
  CONFIRM_STAY,
  CALCULATE_EXTEND_FEE,
  EXTEND_BOOKING,
  MOVE_BOOKING,
  CALCULATE_CANCEL_PENALTY,
  CANCEL_BOOKING_V2,
  CHANGE_BOOKING_GUESTS,
  UPDATE_RENTAL_ITEMS,
  UPDATE_PARKINGS,
  UPDATE_BOOKING_INFO,
  CALCULATE_MOVE_FEE
} from '@/api/graphql/usage-history'
import { BookingTypeIds as bookingTypeId } from '../../../constants/booking-type'
import { getFromUrl, handleSecurePaymentResponse } from '@/utils/secure-payment'

// must freeze the `today` at the time of page load
const recentToDays = Number(process.env.VUE_APP_BOOKING_RECENT_TO_DAYS || '30')
const date = new Date()
const today = isoDate(date)
date.setDate(date.getDate() - 1)
const yesterday = isoDate(date)
const date2 = new Date()
date2.setDate(date2.getDate() + recentToDays)
const recentToDate = isoDate(date2)
date2.setDate(date2.getDate() + 1)
const recentToDatePlus1 = isoDate(date2)

export default {
  async loadNearFutureBookings ({ commit }) {
    const res = await apolloClient.query({
      query: LIST_NEAR_FUTURE_BOOKINGS,
      fetchPolicy: 'no-cache',
      variables: {
        today,
        recentToDate
      }
    })

    const bookings = res.data.myBookingList.items
    commit('setNearFutureBookings', bookings)
  },
  async loadFutureBookings ({ commit }) {
    const res = await apolloClient.query({
      query: LIST_FUTURE_BOOKINGS,
      fetchPolicy: 'no-cache',
      variables: {
        recentToDatePlus1
      }
    })

    const bookings = res.data.myBookingList.items
    commit('setFutureBookings', bookings)
  },
  async loadPastFutureBookings ({ commit, state }) {
    const res = await apolloClient.query({
      query: LIST_PAST_BOOKINGS,
      variables: {
        yesterday,
        bookingTypeId: bookingTypeId.VS,
        skip: state.pastBookings?.length ?? 0,
        take: 10
      },
      fetchPolicy: 'no-cache'
    })

    const bookings = res.data.myBookingList.items
    commit('setPastBookings', (state.pastBookings ?? []).concat(bookings))
  },
  async loadBookingDetail ({ commit }, { id, code }) {
    let res
    if (code) {
      res = await apolloClient.query({
        query: GET_BOOKING_WITH_CODE,
        variables: {
          bookingId: id,
          code
        },
        fetchPolicy: 'no-cache'
      })
    } else {
      res = await apolloClient.query({
        query: GET_BOOKING,
        variables: {
          bookingId: id
        },
        fetchPolicy: 'no-cache'
      })
    }

    const booking = res.data.myBooking
    booking.prePaymentInfo = res.data.getMyPrePaymentInfo
    commit('setSelectedBooking', booking)
    return booking
  },
  async confirmStay (_ctx, { id }) {
    const res = await apolloClient.mutate({
      mutation: CONFIRM_STAY,
      variables: {
        bookingId: id
      }
    })

    return res.data.confirmMyStay
  },

  async preCheckIn (_ctx, { id, code, guests }) {
    const varGuests = guests.map((g) => {
      return {
        name: g.name,
        guestId: g.id,
        address1: g.address1,
        occupation: g.occupation,
        age: g.age,
        sex: g.sex,
        tel: g.tel,
        email: g.email,
        nationality: g.nationality,
        passportNumber: g.passportNumber,
        passportPhoto: g.passportPhoto
      }
    })
    if (code) {
      await apolloClient.mutate({
        mutation: PRE_CHECK_IN_WITH_CODE,
        variables: {
          bookingId: id,
          code,
          guests: varGuests
        }
      })
    } else {
      await apolloClient.mutate({
        mutation: PRE_CHECK_IN,
        variables: {
          bookingId: id,
          guests: varGuests
        }
      })
    }
  },

  async calculateExtendFee (_ctx, {
    id, days
  }) {
    const res = await apolloClient.query({
      query: CALCULATE_EXTEND_FEE,
      variables: {
        bookingId: id,
        days
      }
    })

    const result = res.data.checkPriceForMyBookingExtension
    let accommodationFeePrice = 0 // only the accommodation fee, not including the additional fees
    let accommodationPrice = 0
    let accommodationPriceSp = 0
    let accommodationPriceFp = 0
    result.days.forEach(day => {
      if (days.includes(day.stayDate)) {
        accommodationFeePrice += day.accommodationPrice ?? 0
        accommodationPrice += day.accommodationPrice ?? 0
        accommodationPriceSp += day.accommodationPriceSp ?? 0
        accommodationPriceFp += day.accommodationPriceFp ?? 0
      }
    })
    result.totalAdditional = {
      accommodationFeePrice,
      accommodationPrice,
      accommodationPriceSp,
      accommodationPriceFp
    }

    return result
  },

  async calculateMoveFee(_ctx, {
    args,
    oldCheckInDate,
    oldCheckOutDate
  }) {
    const res = await apolloClient.query({
      query: CALCULATE_MOVE_FEE,
      variables: {
        bookingId: args.bookingId,
        newCheckInDate: args.newCheckInDate,
        newCheckOutDate: args.newCheckOutDate
      },
      fetchPolicy: 'no-cache'
    })

    const result = res.data.checkPriceForMyBookingMove
    let accommodationFeePrice = 0 // only the accommodation fee, not including the additional fees
    let accommodationPrice = 0
    let accommodationPriceSp = 0
    let accommodationPriceFp = 0
    result.days.forEach(day => {
      accommodationFeePrice += day.accommodationPrice ?? 0
      accommodationPrice += day.accommodationPrice ?? 0
      accommodationPriceSp += day.accommodationPriceSp ?? 0
      accommodationPriceFp += day.accommodationPriceFp ?? 0
      accommodationPrice += day.fees?.reduce((sum, fee) => sum + fee.priceWithSalesTax ?? 0, 0) ?? 0
    })

    let penaltyPrice = 0
    let penaltyPriceSp = 0
    let penaltyPriceFp = 0
    result.cancelPenalties.forEach(day => {
      penaltyPrice += day.penaltyPrice
      penaltyPriceSp += day.penaltySpPoint
      penaltyPriceFp += day.penaltyFpPoint
    });

    result.totalAccommodation = {
      accommodationFeePrice,
      accommodationPrice,
      accommodationPriceSp,
      accommodationPriceFp
    }

    result.totalCancelFee = {
      penaltyPrice,
      penaltyPriceSp,
      penaltyPriceFp,
    }

    result.hasReservaionExensionError = 
      (result.parkingReservation.hasTarget && !result.parkingReservation.canExtend) ||
      (result.rentalItemReservation.hasTarget && !result.rentalItemReservation.canExtend);
    result.showReservationExtensionCheck = 
      (result.parkingReservation.hasTarget || result.rentalItemReservation.hasTarget) &&
      (!result.rentalItemReservation.hasTarget || result.rentalItemReservation.canExtend) &&
      (!result.parkingReservation.hasTarget || result.parkingReservation.canExtend);

    return result
  },

  async extendBooking (_ctx, {
    id, days, password, newPrePayment
  }) {
    const res = await apolloClient.mutate({
      mutation: EXTEND_BOOKING,
      variables: {
        bookingId: id,
        days,
        password,
        newPrePayment
      }
    })

    return res.extendMyBooking
  },

  async moveBooking (_ctx, {
    args,
    password, newPrePayment, cancelUseTvp, cancelPaymentMethod
  }) {
    const res = await apolloClient.mutate({
      mutation: MOVE_BOOKING,
      variables: {
        password,
        bookingId: args.bookingId,
        newCheckInDate: args.newCheckInDate,
        newCheckOutDate: args.newCheckOutDate,
        extendParking: args.extendParking,
        extendRentalItems: args.extendRentalItems,
        newPrePayment,
        cancelUseTvp,
        cancelPaymentMethod
      }
    })

    return res.extendMyBooking
  },

  async calculateCancelPenalty (_ctx, {
    bookingId,
    cancelDates
  }) {
    const res = await apolloClient.mutate({
      mutation: CALCULATE_CANCEL_PENALTY,
      variables: {
        bookingId,
        cancelDates
      }
    })

    return res.data.calculateMyBookingCancelPenalty
  },
  async cancelBooking (_ctx, {
    bookingId,
    cancelDates,
    password,
    useTvp,
    paymentMethod,
    newPrePayment
  }) {
    const res = await apolloClient.mutate({
      mutation: CANCEL_BOOKING_V2,
      variables: {
        bookingId,
        cancelDates,
        password,
        useTvp,
        paymentMethod,
        newPrePayment,
        fromUrl: getFromUrl()
      }
    })

    const data = res.data.cancelMyBookingV2

    if (handleSecurePaymentResponse(data)) {
      // Handle successful secure payment
      return null
    } else {
      return data
    }
  },

  async calculateUpdateBookingGuests (_ctx, {
    booking,
    guests
  }) {
    const res = await apolloClient.query({
      query: CALCULATE_PRICE,
      variables: {
        contractId: booking.contract.id,
        roomTypeId: booking.room.roomTypeId,
        checkInDate: booking.checkInDate,
        checkOutDate: booking.checkOutDate,
        numberOfAdults: guests.filter(g => !g.child).length,
        numberOfChildren: guests.filter(g => g.child && !g.bedShared).length,
        numberOfBedShare: guests.filter(g => g.child && g.bedShared).length,
        bookingPlanId: booking.bookingPlanId,
        bookingId: booking.id
      },
      fetchPolicy: 'no-cache'
    })
    const pricesByDay = res.data.calculateMyAccommodationPrice.pricesByDay
    let totalAccommodationFee = 0
    let totalFee = 0
    for (const day of pricesByDay) {
      totalAccommodationFee += day.accommodationFee
      totalFee += day.fees?.reduce((sum, fee) => sum + fee.priceWithSalesTax ?? 0, 0) ?? 0
    }
    return {
      totalPrice: totalAccommodationFee, // this will be always JPY for VS. for others this can be either point or jpy
      totalFee, // this is always jpy
      pricesByDay
    }
  },

  async updateBookingGuests (_ctx, {
    password,
    bookingId,
    guests,
    newPrePayment
  }) {
    const map = (g) => ({
      name: g.name,
      kana: g.kana,
      child: g.child,
      bedShared: g.bedShared,
      address1: g.address1,
      occupation: g.occupation,
      age: g.age,
      sex: g.sex,
      tel: g.tel,
      email: g.email,
      nationality: g.nationality,
      passportNumber: g.passportNumber,
      passportPhoto: g.passportPhoto
    })

    const res = await apolloClient.mutate({
      mutation: CHANGE_BOOKING_GUESTS,
      variables: {
        password,
        bookingId,
        guests: guests.filter(g => !g.representative).map(map),
        representative: map(guests.find(g => g.representative)),
        newPrePayment
      }
    })
    return res.data.updateMyBookingGuests
  },

  async updateRentalItems (_ctx, {
    bookingId,
    rentalItems
  }) {
    const res = await apolloClient.mutate({
      mutation: UPDATE_RENTAL_ITEMS,
      variables: {
        bookingId,
        rentalItems: flattenRentalItemRequest(rentalItems)
      }
    })
    return res.data.updateMyBookingRentalItems
  },

  async updateParkings (_ctx, {
    bookingId,
    parkings
  }) {
    if (typeof parkings !== 'number') {
      // parkings are managed in this facility
      const res = await apolloClient.mutate({
        mutation: UPDATE_PARKINGS,
        variables: {
          bookingId,
          parkings: flattenRentalParkingRequest(parkings, true)
        }
      })
      return res.data.updateMyBookingParking
    } else {
      const res = await apolloClient.mutate({
        mutation: UPDATE_BOOKING_INFO,
        variables: {
          bookingId,
          data: {
            parkingRequestedNumber: parkings
          }
        }
      })
      return res.data.updateMyBooking
    }
  },

  async updatePetInfo (_ctx, {
    bookingId,
    petInfo
  }) {
    if (petInfo) {
      const res = await apolloClient.mutate({
        mutation: UPDATE_BOOKING_INFO,
        variables: {
          bookingId,
          data: {
            petInfo: petInfo
          }
        }
      })
      return res.data.updateMyBooking
    }
  },

  async updatePrePayment (_ctx, {
    bookingId,
    prePayment
  }) {
    if (typeof prePayment === 'number') {
      const res = await apolloClient.mutate({
        mutation: UPDATE_BOOKING_INFO,
        variables: {
          bookingId,
          data: {
            bookingPrePayment: {
              amount: prePayment
            }
          }
        }
      })
      return res.data.updateMyBooking
    }
  }
}
